import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Plus, Trash2 } from 'lucide-react';
import API_BASE_URL from '../config/api';

interface EditableChoice {
  id?: number;
  letter: string;
  text: string;
  isCorrect: boolean;
}

interface EditableQuestion {
  id?: number;
  questionNumber: number;
  questionText: string;
  points: number;
  part_id: number | null;
  answerChoices: EditableChoice[];
  isNew?: boolean;
  markedForDeletion?: boolean;
}

interface Part {
  id: number;
  titre: string;
  ordre: number;
}

const allLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

const EditDetailedTest: React.FC = () => {
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();

  const [testTitle, setTestTitle] = useState<string>('');
  const [parts, setParts] = useState<Part[]>([]);
  const [questions, setQuestions] = useState<EditableQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchTest();
  }, [testId]);

  const fetchTest = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) { navigate('/login'); return; }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${testId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur chargement');

      const data = await response.json();
      setTestTitle(data.test.title);
      setParts(data.test.parts || []);

      const loadedQuestions: EditableQuestion[] = (data.test.questions || []).map((q: any) => ({
        id: q.id,
        questionNumber: q.question_number,
        questionText: q.question_text || '',
        points: q.points || 1,
        part_id: q.part_id || null,
        answerChoices: (q.answer_choices || []).map((c: any) => ({
          id: c.id,
          letter: c.choice_letter,
          text: c.choice_text || '',
          isCorrect: c.is_correct || false
        }))
      }));

      setQuestions(loadedQuestions);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  // Questions visibles (non marquées pour suppression)
  const visibleQuestions = questions.filter(q => !q.markedForDeletion);
  const currentQuestion = visibleQuestions[currentIndex];

  const updateQuestionText = (text: string) => {
    const q = visibleQuestions[currentIndex];
    setQuestions(prev => prev.map(item => item === q ? { ...item, questionText: text } : item));
  };

  const updatePoints = (points: number) => {
    const q = visibleQuestions[currentIndex];
    setQuestions(prev => prev.map(item => item === q ? { ...item, points: points > 0 ? points : 1 } : item));
  };

  const updateChoiceText = (choiceIndex: number, text: string) => {
    const q = visibleQuestions[currentIndex];
    setQuestions(prev => prev.map(item => {
      if (item !== q) return item;
      const newChoices = [...item.answerChoices];
      newChoices[choiceIndex] = { ...newChoices[choiceIndex], text };
      return { ...item, answerChoices: newChoices };
    }));
  };

  const toggleCorrect = (choiceIndex: number) => {
    const q = visibleQuestions[currentIndex];
    setQuestions(prev => prev.map(item => {
      if (item !== q) return item;
      const newChoices = [...item.answerChoices];
      newChoices[choiceIndex] = { ...newChoices[choiceIndex], isCorrect: !newChoices[choiceIndex].isCorrect };
      return { ...item, answerChoices: newChoices };
    }));
  };

  const addChoice = () => {
    const q = visibleQuestions[currentIndex];
    if (q.answerChoices.length >= 10) return;
    setQuestions(prev => prev.map(item => {
      if (item !== q) return item;
      const newChoices = [...item.answerChoices, {
        letter: allLetters[item.answerChoices.length],
        text: '',
        isCorrect: false
      }];
      return { ...item, answerChoices: newChoices };
    }));
  };

  const removeChoice = (choiceIndex: number) => {
    const q = visibleQuestions[currentIndex];
    if (q.answerChoices.length <= 2) return;
    setQuestions(prev => prev.map(item => {
      if (item !== q) return item;
      const newChoices = item.answerChoices.filter((_, i) => i !== choiceIndex);
      newChoices.forEach((c, i) => { c.letter = allLetters[i]; });
      return { ...item, answerChoices: newChoices };
    }));
  };

  const addQuestion = () => {
    const newQ: EditableQuestion = {
      questionNumber: visibleQuestions.length + 1,
      questionText: '',
      points: 1,
      part_id: null,
      answerChoices: [
        { letter: 'A', text: '', isCorrect: false },
        { letter: 'B', text: '', isCorrect: false },
        { letter: 'C', text: '', isCorrect: false },
        { letter: 'D', text: '', isCorrect: false }
      ],
      isNew: true
    };
    setQuestions(prev => [...prev, newQ]);
    setCurrentIndex(visibleQuestions.length);
  };

  const requestDeleteQuestion = () => {
    setDeleteConfirmIndex(currentIndex);
  };

  const confirmDeleteQuestion = () => {
    const q = visibleQuestions[deleteConfirmIndex!];
    if (q.isNew) {
      // Supprimer directement de la liste locale
      setQuestions(prev => prev.filter(item => item !== q));
    } else {
      // Marquer pour suppression backend
      setQuestions(prev => prev.map(item => item === q ? { ...item, markedForDeletion: true } : item));
    }
    const newVisible = visibleQuestions.filter((_, i) => i !== deleteConfirmIndex!);
    setCurrentIndex(Math.min(deleteConfirmIndex!, Math.max(0, newVisible.length - 1)));
    setDeleteConfirmIndex(null);
  };

  const isCurrentQuestionValid = (): boolean => {
    if (!currentQuestion) return false;
    if (!currentQuestion.questionText.trim()) return false;
    for (const c of currentQuestion.answerChoices) {
      if (!c.text.trim()) return false;
    }
    return currentQuestion.answerChoices.some(c => c.isCorrect);
  };

  const areAllQuestionsValid = (): boolean => {
    return visibleQuestions.every(q => {
      if (!q.questionText.trim()) return false;
      for (const c of q.answerChoices) {
        if (!c.text.trim()) return false;
      }
      return q.answerChoices.some(c => c.isCorrect);
    });
  };

  const handleSave = async () => {
    if (!areAllQuestionsValid()) {
      alert('Veuillez remplir tous les champs et marquer au moins une bonne réponse par question.');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('authToken');
    if (!token) { navigate('/login'); return; }

    try {
      // 1. Supprimer les questions marquées
      const toDelete = questions.filter(q => q.markedForDeletion && !q.isNew && q.id);
      for (const q of toDelete) {
        await fetch(`${API_BASE_URL}/api/tests/questions/${q.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      // 2. Ajouter les nouvelles questions
      const toAdd = visibleQuestions.filter(q => q.isNew);
      for (const q of toAdd) {
        await fetch(`${API_BASE_URL}/api/tests/${testId}/questions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            questionText: q.questionText,
            points: q.points,
            partId: q.part_id,
            answerChoices: q.answerChoices.map(c => ({
              letter: c.letter,
              text: c.text,
              isCorrect: c.isCorrect
            }))
          })
        });
      }

      // 3. Mettre à jour les questions existantes
      const toUpdate = visibleQuestions.filter(q => !q.isNew && q.id);
      for (const q of toUpdate) {
        // Mettre à jour la question
        await fetch(`${API_BASE_URL}/api/tests/questions/${q.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ questionText: q.questionText, points: q.points })
        });

        // Mettre à jour les choix existants
        for (const c of q.answerChoices) {
          if (c.id) {
            await fetch(`${API_BASE_URL}/api/tests/answer-choices/${c.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ choiceText: c.text, isCorrect: c.isCorrect })
            });
          }
        }
      }

      navigate('/my-tests');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Chargement...</p>
      </div>
    );
  }

  if (visibleQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
        <header className="bg-white shadow-md">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
            <button onClick={() => navigate('/my-tests')} className="text-gray-600 hover:text-gray-800">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Éditer le test : {testTitle}</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-white rounded-lg shadow-2xl p-8 text-center">
            <p className="text-gray-600 mb-6">Aucune question dans ce test.</p>
            <button
              onClick={addQuestion}
              className="flex items-center gap-2 mx-auto bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              <Plus className="w-5 h-5" />
              Ajouter une question
            </button>
          </div>
        </main>
      </div>
    );
  }

  const partLabel = parts.find(p => p.id === currentQuestion?.part_id)?.titre;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/my-tests')} className="text-gray-600 hover:text-gray-800 transition">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Éditer : {testTitle}</h1>
          </div>
          <button
            onClick={handleSave}
            disabled={!areAllQuestionsValid() || saving}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition ${
              !areAllQuestionsValid() || saving
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <Check className="w-5 h-5" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">
                Question {currentIndex + 1} sur {visibleQuestions.length}
              </span>
              <button
                onClick={addQuestion}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium transition"
              >
                <Plus className="w-4 h-4" />
                Ajouter une question
              </button>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / visibleQuestions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Part label */}
          {partLabel && (
            <div className="mb-6 bg-blue-50 border-l-4 border-blue-600 px-4 py-3 rounded">
              <p className="text-sm font-medium text-blue-800">Partie : {partLabel}</p>
            </div>
          )}

          {/* Question form */}
          <div className="space-y-6">
            {/* Question text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre de la question {currentQuestion.questionNumber} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={currentQuestion.questionText}
                onChange={(e) => updateQuestionText(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Entrez le texte de la question..."
              />
            </div>

            {/* Points */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de points
              </label>
              <input
                type="number"
                min="1"
                value={currentQuestion.points}
                onChange={(e) => updatePoints(parseInt(e.target.value) || 1)}
                className="w-24 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Answer choices */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Choix de réponses <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500 ml-2">(Cochez au moins une bonne réponse)</span>
              </label>
              <div className="space-y-4">
                {currentQuestion.answerChoices.map((choice, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={choice.isCorrect}
                      onChange={() => toggleCorrect(idx)}
                      className="mt-3 w-5 h-5 text-blue-600"
                    />
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Choix {choice.letter} {choice.isCorrect && <span className="text-green-600">✓ Correct</span>}
                      </label>
                      <input
                        type="text"
                        value={choice.text}
                        onChange={(e) => updateChoiceText(idx, e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Texte pour le choix ${choice.letter}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeChoice(idx)}
                      disabled={currentQuestion.answerChoices.length <= 2}
                      title="Supprimer ce choix"
                      className={`mt-8 p-1 rounded transition ${
                        currentQuestion.answerChoices.length <= 2
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-red-500 hover:text-red-700'
                      }`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              {currentQuestion.answerChoices.length < 10 && (
                <button
                  type="button"
                  onClick={addChoice}
                  className="mt-3 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un choix
                </button>
              )}
            </div>

            {/* Validation message */}
            {!isCurrentQuestionValid() && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">⚠️ Pour continuer, veuillez :</p>
                <ul className="text-xs text-yellow-700 mt-2 ml-4 list-disc">
                  <li>Remplir le titre de la question</li>
                  <li>Remplir tous les textes des choix</li>
                  <li>Marquer au moins un choix comme correct</li>
                </ul>
              </div>
            )}
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t">
            <button
              onClick={() => setCurrentIndex(i => i - 1)}
              disabled={currentIndex === 0}
              className={`px-6 py-3 rounded-lg font-medium transition ${
                currentIndex === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
            >
              ← Précédent
            </button>

            <button
              onClick={requestDeleteQuestion}
              className="flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-red-600 border border-red-300 hover:bg-red-50 transition"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer cette question
            </button>

            <button
              onClick={() => setCurrentIndex(i => i + 1)}
              disabled={currentIndex === visibleQuestions.length - 1 || !isCurrentQuestionValid()}
              className={`px-6 py-3 rounded-lg font-medium transition ${
                currentIndex === visibleQuestions.length - 1 || !isCurrentQuestionValid()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Suivant →
            </button>
          </div>
        </div>
      </main>

      {/* Modal de confirmation de suppression */}
      {deleteConfirmIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Supprimer la question ?</h3>
            <p className="text-gray-600 mb-2">
              Voulez-vous supprimer la question {visibleQuestions[deleteConfirmIndex]?.questionNumber} ?
            </p>
            <p className="text-red-600 text-sm mb-6">
              Cette action supprimera également toutes les réponses des étudiants pour cette question.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmIndex(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteQuestion}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditDetailedTest;
